import { Store } from 'hooks/createStore';
import {
  MouseEvent,
  TouchEvent,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { DocumentationProgressBox } from './DocumentationProgressBox';
import { DocumentationProgressPinButton } from './DocumentationProgressPinButton';
import { ProgressModel } from './types';
import { createStyles, makeStyles } from '@mui/styles';

export const useStyles = makeStyles(() =>
  createStyles({
    documentationProgressRoot: {
      height: '100%',
      width: '64px',
      flexBasis: '64px',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1,
      position: 'relative',
    },
    expandable: {
      position: 'absolute',
      right: 0,
      top: 0,
      display: 'flex',
      flexDirection: 'column',
      flexFlow: 'column nowrap',
      height: '100%',
      overflow: 'visible',
      backgroundColor: '#FFFFFF',
      transition: 'width 100ms cubic-bezier(0.4, 0.0, 0.2, 1)',
      boxShadow: '0px 2px 9px 0px rgba(0, 0, 0, 0.25)',
    },
    panelHeader: {
      height: '72px',
      width: '100%',
      borderBottom: '1px solid #00000029',
      boxSizing: 'border-box',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      paddingRight: '0.75rem',
    },
    progressContainer: {
      height: '100%',
      width: '100%',
      overflowY: 'auto',
      boxSizing: 'border-box',
      overflowX: 'hidden',
    },
  })
);

const stopPropagation = (e: TouchEvent | MouseEvent): void =>
  e.stopPropagation();

export function DocumentationProgress({
  activityStore,
  progress,
  rightPanelVisibleStore,
}: {
  activityStore: Store<boolean>;
  progress: ProgressModel[];
  rightPanelVisibleStore: Store<boolean>;
}): ReactElement | null {
  const classes = useStyles();
  const active = useActivityFromStore(activityStore);
  const {
    expanded,
    setExpandedTrue,
    setExpandedFalse,
    pinned,
    togglePinned,
  } = useExpanded(activityStore, active);

  useEffect(() => {
    rightPanelVisibleStore.set(true);
  }, [rightPanelVisibleStore]);

  return (
    <div
      className={classes.documentationProgressRoot}
      onMouseEnter={setExpandedTrue}
      onMouseMove={setExpandedTrue}
      onMouseLeave={setExpandedFalse}
      // prevents ClickawayListener
      onClick={stopPropagation}
      onTouchEnd={stopPropagation}
    >
      <div
        className={classes.expandable}
        style={{
          width: expanded ? '194px' : '64px',
        }}
      >
        <div className={classes.panelHeader}>
          <DocumentationProgressPinButton
            pinned={pinned}
            onClick={togglePinned}
          />
        </div>
        <div className={classes.progressContainer}>
          <Progress
            active={active}
            expanded={expanded}
            progress={progress}
          />
        </div>
      </div>
    </div>
  );
}

function useActivityFromStore(activityStore: Store<boolean>): boolean {
  const [active, setActive] = useState(activityStore.get());

  useEffect(() => {
    setActive(activityStore.get());
    return activityStore.subscribe(() => {
      setActive(activityStore.get());
    });
  }, [activityStore]);

  return active;
}

function useExpanded(
  activityStore: Store<boolean>,
  active: boolean
): {
  expanded: boolean;
  setExpandedTrue: () => void;
  setExpandedFalse: () => void;
  pinned: boolean;
  togglePinned: () => void;
} {
  const [pinned, setPinned] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const setExpandedTrue = useCallback(() => {
    setExpanded(activityStore.get());
  }, [activityStore]);

  const setExpandedFalse = useCallback(() => {
    setExpanded(activityStore.get() && pinned);
  }, [activityStore, pinned]);

  const togglePinned = useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      setExpanded(activityStore.get() && next);
      return next;
    });
  }, [activityStore]);

  return {
    expanded,
    setExpandedTrue,
    setExpandedFalse,
    pinned,
    togglePinned,
  };
}

function Progress({
  active,
  expanded,
  progress,
}: {
  active: boolean;
  expanded: boolean;
  progress: ProgressModel[];
}): ReactElement | null {
  if (!active) return null;
  return <ProgressBoxes expanded={expanded} progress={progress} />;
}

function ProgressBoxes({
  expanded,
  progress,
}: {
  expanded: boolean;
  progress: ProgressModel[];
}): ReactElement {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {progress.map((progress, index) => {
        return (
          <DocumentationProgressBox
            key={progress._id}
            no={`${index + 1}`}
            progressId={progress._id}
            done={progress.done}
            max={progress.max}
            expanded={expanded}
          />
        );
      })}
    </div>
  );
}
